import { TypedDocumentNode } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { DoorBatteryWarningLevel, Query } from 'graphql/generated/graphqlTypes';
import { GET_DOORS_FOR_TENANT } from 'graphql/v2/queries';
import { BaseHookProps } from 'hooks/shared/types';
import { useRegisterBackgroundRefetch } from 'hooks/useBackgroundRefetch/useBackgroundRefetch';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { useEffect, useMemo } from 'react';

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
const GET_DOORS_FOR_TENANT_TYPED: TypedDocumentNode<Pick<Query, 'getDoorsForTenant'>> = GET_DOORS_FOR_TENANT;

const useDashboardBatteries = ({ handleFetchError }: BaseHookProps) => {
  const { translation } = useTranslation();

  const {
    loading: doorsLoading,
    error: doorsError,
    data: doorsData
  } = useQuery(GET_DOORS_FOR_TENANT_TYPED, {
    notifyOnNetworkStatusChange: true
  });

  useEffect(() => {
    if (doorsError) {
      handleFetchError(translation.devices_error_fetching);
      console.error(doorsError);
    }
  }, [doorsError]);

  // `loading` is used instead of the network status because a failed refetch leaves the status on `error`, which would
  // keep the "refreshing" indicator on until the next success.
  const isRefetchingDashboardBatteries = !!doorsData && doorsLoading;

  useRegisterBackgroundRefetch(isRefetchingDashboardBatteries);

  const batteryCounts = useMemo(() => {
    const doors = doorsData?.getDoorsForTenant ?? [];

    return doors.reduce(
      (acc, door) => {
        switch (door.batteryWarningLevel) {
          case DoorBatteryWarningLevel.BatteryEmpty:
            acc.batteriesEmpty++;
            break;
          case DoorBatteryWarningLevel.BatteryCritical:
            acc.batteriesCritical++;
            break;
          case DoorBatteryWarningLevel.BatteryLow:
            acc.batteriesLow++;
            break;
          case DoorBatteryWarningLevel.BatteryUnknown:
            acc.batteriesUnknown++;
            break;
        }

        return acc;
      },
      {
        batteriesEmpty: 0,
        batteriesCritical: 0,
        batteriesLow: 0,
        batteriesUnknown: 0
      }
    );
  }, [doorsData]);

  return {
    ...batteryCounts,
    totalDoors: doorsData?.getDoorsForTenant.length ?? 0,
    loadingDashboardBatteries: doorsLoading && !doorsData,
    isRefetchingDashboardBatteries
  };
};

export default useDashboardBatteries;
